import { Layout as CompLayout } from "@shared/ui";

import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { ROUTES } from "@app/constants";
import { BreifInfluencerV2StatusEnum } from "@entities/brief/lib/constants";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useGetBriefInfluencerSearchFacetsByStatus } from "@entities/brief/lib/hooks/useGetBriefInfluencerSearchFacetsByStatus";
import { CampaignManagersTeamButton } from "@features/CampaignManagers/ui/CampaignManagersTeamButton";
import { CampaignTabs, Tab } from "@widgets/CampaignTabs";
import { PageHeader } from "@widgets/PageHeader";
import { Row, Spin } from "antd";
import { useEffect, useState } from "react";
import { useBriefStore } from "../lib/useLayoutStore";

const { Content } = CompLayout;

export type CampaignStatus =
  | "Pending"
  | "Briefing"
  | "In Execution"
  | "Completed";

export function BriefLayout() {
  const { briefId } = useParams();
  const { setBrief, setIsLoadingBrief } = useBriefStore();
  const location = useLocation();
  const navigate = useNavigate();

  const { brief, isFetching, isFetchingBrief, refetch } = useGetBrIefById(
    briefId || ""
  );

  const { searchFacets } = useGetBriefInfluencerSearchFacetsByStatus(
    briefId || "",
    BreifInfluencerV2StatusEnum.All
  );

  const [campaignStatus, setCampaignStatus] =
    useState<CampaignStatus>("Pending");

  const getActiveTab = (): Tab => {
    const path = location.pathname;
    if (path.includes("/overview")) return "overview";
    if (path.includes("/campaigns")) return "execution";
    if (path.includes("/report")) return "reports";
    if (path.includes("/billing")) return "finance";
    if (path.includes("/ai-overview")) return "ai-overview";
    return "brief";
  };

  const handleTabChange = (tab: Tab) => {
    if (!briefId) return;
    switch (tab) {
      case "overview":
        navigate(ROUTES.BRIEF_V2_OVERVIEW.replace(":briefId", briefId));
        break;
      case "brief":
        navigate(ROUTES.BRIEF_V2.replace(":briefId", briefId));
        break;
      case "execution":
        navigate(ROUTES.BRIEF_V2_CAMPAIGNS.replace(":briefId", briefId));
        break;
      case "reports":
        navigate(ROUTES.BRIEF_V2_REPORT.replace(":briefId", briefId));
        break;
      case "finance":
        navigate(ROUTES.BRIEF_V2_BILL.replace(":briefId", briefId));
        break;
      case "ai-overview":
        navigate(ROUTES.BRIEF_V2_AI_OVERVIEW.replace(":briefId", briefId));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (briefId && brief) {
      setBrief(brief, briefId);
      document.title = brief.name;

      // Map brief status to campaign status
      if (brief?.status || brief?.mainStatus) {
        if (brief?.status === "Completed") {
          setCampaignStatus("Completed");
        } else {
          const status = brief?.mainStatus || "";
          if (status.includes("Pending")) {
            setCampaignStatus("Pending");
          } else if (status === "Brief") {
            setCampaignStatus("Briefing");
          } else if (status === "Campaign") {
            setCampaignStatus("In Execution");
          }
        }
      }

      // Update meta description to brief name
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", brief.name);
      } else {
        const newMetaDescription = document.createElement("meta");
        newMetaDescription.name = "description";
        newMetaDescription.content = brief.name;
        document.head.appendChild(newMetaDescription);
      }
    }
  }, [briefId, brief, setBrief]);

  useEffect(() => {
    return () => {
      document.title = "Cloutflow";
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", "Cloutflow");
      }
    };
  }, []);

  useEffect(() => {
    if (isFetchingBrief) {
      setIsLoadingBrief(true);
    } else {
      setIsLoadingBrief(false);
    }
  }, [isFetchingBrief]);

  const campaignTitle = brief?.name || "Campaign";

  return (
    <div style={{ width: "100%", margin: "0 auto" }}>
      {isFetching ? (
        <Row justify="center" align="middle" style={{ height: "100%" }}>
          <Spin />
        </Row>
      ) : (
        <div>
          <div style={{ marginBottom: 20 }}>
            <PageHeader
              title={campaignTitle}
              backText="Campaigns"
              backTo={ROUTES.BRIEF}
              extra={<CampaignManagersTeamButton />}
            />
            <CampaignTabs
              activeTab={getActiveTab()}
              onTabChange={handleTabChange}
              briefCount={searchFacets?.inBrief || 0}
              executionCount={searchFacets?.inExecution || 0}
            />
          </div>

          <Outlet />
        </div>
      )}
    </div>
  );
}
